import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import {
  Crown, DollarSign, Users, Settings as SettingsIcon, ArrowLeft, Loader2, Save,
  BarChart3, FileText, Megaphone, Tag, Cog, Plus, Trash2, Star, StarOff,
  Eye, EyeOff, Ban, ShieldCheck, CheckCircle2, XCircle, Image as ImageIcon,
} from "lucide-react";

const TABS = [
  { key: "overview",   label: "Overview",       icon: BarChart3 },
  { key: "site",       label: "Site Settings",  icon: ImageIcon },
  { key: "content",    label: "Content",        icon: FileText },
  { key: "ads",        label: "Banner Ads",     icon: Megaphone },
  { key: "users",      label: "Users",          icon: Users },
  { key: "pricing",    label: "Pricing",        icon: Tag },
  { key: "engine",     label: "Auto-Content",   icon: Cog },
];

export default function OwnerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [toast, setToast] = useState({ msg: "", tone: "ok" });
  const showToast = (msg, tone = "ok") => { setToast({ msg, tone }); setTimeout(() => setToast({ msg: "", tone }), 3200); };

  // Route guard — only OWNER role
  if (user && user.role !== "owner") return <Navigate to="/app/dashboard" replace />;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {toast.msg && (
        <div
          data-testid="owner-toast"
          className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-md font-medium shadow-2xl text-sm ${
            toast.tone === "err" ? "bg-red-500 text-white" : "bg-sky-400 text-slate-950"
          }`}
        >
          {toast.msg}
        </div>
      )}

      <header className="border-b border-slate-800 px-6 py-4 flex items-center justify-between sticky top-0 bg-slate-950/95 backdrop-blur z-40">
        <div className="flex items-center gap-4">
          <button
            data-testid="btn-back-admin"
            onClick={() => navigate("/admin")}
            className="text-slate-500 hover:text-sky-300 flex items-center gap-1 text-xs font-mono uppercase tracking-widest"
          >
            <ArrowLeft className="w-3 h-3" /> Admin
          </button>
          <div className="border-l border-slate-800 pl-4">
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-sky-400" />
              <h1 className="font-serif text-xl text-sky-100">Owner Console</h1>
              <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border border-sky-400 text-sky-300 bg-sky-500/5">
                super admin
              </span>
            </div>
          </div>
        </div>
        <button
          data-testid="btn-owner-public-site"
          onClick={() => window.open("/", "_blank")}
          className="text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-sky-300"
        >
          View public site ↗
        </button>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 pt-6">
        {/* Tab bar */}
        <div className="flex flex-wrap gap-2 border-b border-slate-800 pb-4">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`owner-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest flex items-center gap-2 transition ${
                  active
                    ? "bg-sky-400 text-slate-950"
                    : "bg-slate-900 text-slate-400 border border-slate-800 hover:text-sky-100 hover:border-sky-500/40"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-8 fade-up">
        {tab === "overview" && <OverviewTab showToast={showToast} />}
        {tab === "site"     && <SiteSettingsTab showToast={showToast} />}
        {tab === "content"  && <ContentTab showToast={showToast} />}
        {tab === "ads"      && <AdsTab showToast={showToast} />}
        {tab === "users"    && <UsersTab showToast={showToast} currentUserEmail={user?.email} />}
        {tab === "pricing"  && <PricingTab showToast={showToast} />}
        {tab === "engine"   && <EngineTab showToast={showToast} />}
      </div>
    </div>
  );
}

/* ---------------- Overview (Platform Stats) ---------------- */
function OverviewTab({ showToast }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/owner/stats");
        setStats(data);
      } catch (e) {
        showToast(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load stats", "err");
      }
    })();
  }, []); // eslint-disable-line
  if (!stats) return <LoadingRow label="Loading platform stats…" />;
  const cards = [
    { label: "Total users", value: stats.users.total, sub: `${stats.users.banned} banned · ${stats.users.admins} admins`, testid: "stat-users" },
    { label: "Total articles", value: stats.articles.total, sub: `${stats.articles.public} public · ${stats.articles.editors_picks} picks`, testid: "stat-articles" },
    { label: "Total reads", value: stats.engagement.total_views, sub: `${stats.engagement.total_clicks} clicks`, testid: "stat-reads" },
    { label: "Total commission", value: `$${stats.revenue.total_commission.toFixed(2)}`, sub: `${stats.revenue.active_subscriptions} active subs`, testid: "stat-commission" },
    { label: "Conversions", value: stats.engagement.total_conversions, sub: "affiliate conversions", testid: "stat-conversions" },
    { label: "Banner ads", value: stats.ads.total, sub: "ad slots configured", testid: "stat-ads" },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {cards.map((c) => (
        <div key={c.label} data-testid={c.testid} className="border border-slate-800 rounded-xl p-6 bg-gradient-to-br from-sky-500/5 to-transparent hover:border-sky-500/40 transition">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">{c.label}</p>
          <p className="font-serif text-4xl text-sky-100 leading-none">{c.value}</p>
          <p className="text-xs text-slate-500 mt-2">{c.sub}</p>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Site Settings (title, tagline, logo) ---------------- */
function SiteSettingsTab({ showToast }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { (async () => setSettings((await api.get("/owner/settings")).data))(); }, []);
  if (!settings) return <LoadingRow label="Loading site settings…" />;
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/owner/settings", {
        site_name: settings.site_name,
        site_tagline: settings.site_tagline,
        logo_url: settings.logo_url,
      });
      showToast("Site branding saved.");
    } catch (e) {
      showToast(formatApiErrorDetail(e.response?.data?.detail) || "Save failed", "err");
    } finally { setSaving(false); }
  };
  return (
    <Card icon={ImageIcon} title="Site branding" subtitle="Shown across the public website, browser tab, and navigation shell.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Site title">
          <input
            data-testid="owner-site-name"
            value={settings.site_name || ""}
            onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
            className={inputCls}
          />
        </Field>
        <Field label="Tagline">
          <input
            data-testid="owner-site-tagline"
            value={settings.site_tagline || ""}
            onChange={(e) => setSettings({ ...settings, site_tagline: e.target.value })}
            placeholder="Multi Business Platform"
            className={inputCls}
          />
        </Field>
        <Field label="Logo URL" className="md:col-span-2">
          <input
            data-testid="owner-site-logo"
            value={settings.logo_url || ""}
            onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
            placeholder="https://cdn.example.com/logo.svg"
            className={`${inputCls} font-mono text-xs`}
          />
          {settings.logo_url && (
            <img src={settings.logo_url} alt="logo preview" className="mt-3 max-h-14 rounded border border-slate-800 p-2 bg-slate-900" />
          )}
        </Field>
      </div>
      <SaveButton onClick={save} saving={saving} testid="btn-save-site-settings" />
    </Card>
  );
}

/* ---------------- Content Control ---------------- */
function ContentTab({ showToast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | public | private | picks
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/owner/articles?limit=200");
      setItems(data.items || []);
    } catch (e) {
      showToast(formatApiErrorDetail(e.response?.data?.detail) || "Failed", "err");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const patch = async (id, updates) => {
    try {
      await api.patch(`/owner/articles/${id}`, updates);
      setItems((prev) => prev.map((a) => a.id === id ? { ...a, ...updates } : a));
      showToast("Article updated.");
    } catch (e) {
      showToast(formatApiErrorDetail(e.response?.data?.detail) || "Failed", "err");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this article permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/owner/articles/${id}`);
      setItems((prev) => prev.filter((a) => a.id !== id));
      showToast("Article deleted.");
    } catch (e) {
      showToast(formatApiErrorDetail(e.response?.data?.detail) || "Failed", "err");
    }
  };
  const filtered = items.filter((a) => {
    if (filter === "public") return a.is_public;
    if (filter === "private") return !a.is_public;
    if (filter === "picks") return a.is_editors_pick;
    return true;
  });
  return (
    <Card icon={FileText} title={`Content · ${items.length}`} subtitle="Publish, unpublish, feature as editor's pick, or delete any article across the platform.">
      <div className="flex gap-2 mb-4">
        {[
          { k: "all", l: "All" },
          { k: "public", l: "Public" },
          { k: "private", l: "Private" },
          { k: "picks", l: "Editor's picks" },
        ].map((f) => (
          <button
            key={f.k}
            data-testid={`content-filter-${f.k}`}
            onClick={() => setFilter(f.k)}
            className={`px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest ${
              filter === f.k ? "bg-sky-400 text-slate-950" : "bg-slate-900 text-slate-400 border border-slate-800"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>
      {loading ? <LoadingRow label="Loading articles…" /> : filtered.length === 0 ? (
        <EmptyRow label="No articles match this filter." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900">
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2">Author</th>
                <th className="px-3 py-2">Niche</th>
                <th className="px-3 py-2">Visibility</th>
                <th className="px-3 py-2">Pick</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} data-testid={`owner-article-${a.id}`} className="border-t border-slate-800 hover:bg-slate-900/40">
                  <td className="px-3 py-2 max-w-xs truncate text-sky-100">{a.title}</td>
                  <td className="px-3 py-2 text-xs text-slate-400 font-mono">{a.author_name}</td>
                  <td className="px-3 py-2 text-xs text-slate-400">{a.niche || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${a.is_public ? "border-emerald-500/40 text-emerald-300" : "border-slate-700 text-slate-500"}`}>
                      {a.is_public ? "public" : "private"}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {a.is_editors_pick && <Star className="w-3.5 h-3.5 text-sky-300 fill-sky-300/20" />}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1 justify-end">
                      <IconBtn
                        title={a.is_public ? "Unpublish" : "Publish"}
                        testid={`btn-toggle-public-${a.id}`}
                        onClick={() => patch(a.id, { is_public: !a.is_public })}
                        icon={a.is_public ? EyeOff : Eye}
                      />
                      <IconBtn
                        title={a.is_editors_pick ? "Remove from picks" : "Add to editor's picks"}
                        testid={`btn-toggle-pick-${a.id}`}
                        onClick={() => patch(a.id, { is_editors_pick: !a.is_editors_pick })}
                        icon={a.is_editors_pick ? StarOff : Star}
                      />
                      <IconBtn
                        title="Delete"
                        testid={`btn-delete-article-${a.id}`}
                        onClick={() => remove(a.id)}
                        icon={Trash2}
                        danger
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* ---------------- Banner Ads Management ---------------- */
function AdsTab({ showToast }) {
  const [ads, setAds] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    slot_key: "landing-top", advertiser_name: "", target_url: "", image_url: "",
    image_base64: "", image_preview: "",
    headline: "", subheadline: "", price_usd: 0, active: true,
  });
  const load = async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([api.get("/ads"), api.get("/ads/slots")]);
      setAds(a.data || []);
      setSlots(s.data || []);
      if (s.data?.length && !s.data.find((sl) => sl.key === form.slot_key)) {
        setForm((f) => ({ ...f, slot_key: s.data[0].key }));
      }
    } catch (e) {
      showToast(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load ads", "err");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleImageFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showToast("Only image files are supported", "err"); return;
    }
    if (file.size > 500 * 1024) {
      showToast("Image is too large — please keep under 500KB", "err"); return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setForm((f) => ({ ...f, image_base64: dataUrl, image_preview: dataUrl, image_url: "" }));
    };
    reader.onerror = () => showToast("Could not read file", "err");
    reader.readAsDataURL(file);
  };
  const create = async () => {
    if (!form.advertiser_name || !form.target_url) { showToast("Advertiser name + URL required", "err"); return; }
    if (!form.image_base64) { showToast("Please drop or select an image", "err"); return; }
    setCreating(true);
    try {
      const { image_preview, ...payload } = form;
      await api.post("/ads", payload);
      showToast("Ad created.");
      setForm({ ...form, advertiser_name: "", target_url: "", image_base64: "", image_preview: "", headline: "", subheadline: "" });
      await load();
    } catch (e) {
      showToast(formatApiErrorDetail(e.response?.data?.detail) || "Failed", "err");
    } finally { setCreating(false); }
  };
  const toggle = async (id, active) => {
    try {
      await api.patch(`/ads/${id}`, { active });
      setAds((prev) => prev.map((a) => a.id === id ? { ...a, active } : a));
    } catch (e) { showToast(formatApiErrorDetail(e.response?.data?.detail) || "Failed", "err"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this ad?")) return;
    try {
      await api.delete(`/ads/${id}`);
      setAds((prev) => prev.filter((a) => a.id !== id));
      showToast("Ad deleted.");
    } catch (e) { showToast(formatApiErrorDetail(e.response?.data?.detail) || "Failed", "err"); }
  };
  return (
    <div className="space-y-6">
      <Card icon={Plus} title="Create ad slot" subtitle="Set slot, advertiser, image, and monthly price. Impressions and clicks are tracked automatically.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Slot">
            <select
              data-testid="ad-slot-select"
              value={form.slot_key}
              onChange={(e) => setForm({ ...form, slot_key: e.target.value })}
              className={inputCls}
            >
              {slots.map((s) => <option key={s.key} value={s.key}>{s.label || s.key} · ${s.price_usd}/mo</option>)}
              {slots.length === 0 && <option>landing-top</option>}
            </select>
          </Field>
          <Field label="Advertiser name">
            <input data-testid="ad-advertiser" value={form.advertiser_name} onChange={(e) => setForm({ ...form, advertiser_name: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Target URL" className="md:col-span-2">
            <input data-testid="ad-target-url" value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="https://…" className={`${inputCls} font-mono text-xs`} />
          </Field>
          <Field label="Ad image" className="md:col-span-2">
            <AdImageDropzone
              preview={form.image_preview}
              onFile={handleImageFile}
              onClear={() => setForm({ ...form, image_base64: "", image_preview: "" })}
            />
          </Field>
          <Field label="Monthly price (USD)">
            <input data-testid="ad-price" type="number" min="0" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: Number(e.target.value) })} className={`${inputCls} font-mono`} />
          </Field>
          <Field label="Headline">
            <input data-testid="ad-headline" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Sub-headline">
            <input data-testid="ad-subheadline" value={form.subheadline} onChange={(e) => setForm({ ...form, subheadline: e.target.value })} className={inputCls} />
          </Field>
        </div>
        <SaveButton onClick={create} saving={creating} testid="btn-create-ad" label="Create ad" icon={Plus} />
      </Card>

      <Card icon={Megaphone} title={`Existing ads · ${ads.length}`}>
        {loading ? <LoadingRow label="Loading ads…" /> : ads.length === 0 ? (
          <EmptyRow label="No ads yet. Create your first slot above." />
        ) : (
          <div className="space-y-2">
            {ads.map((ad) => (
              <div key={ad.id} data-testid={`ad-row-${ad.id}`} className="border border-slate-800 rounded-lg p-4 flex items-center gap-4">
                {ad.image_url ? (
                  <img src={ad.image_url} alt={ad.advertiser_name} className="w-20 h-12 object-contain rounded bg-slate-900 border border-slate-800" />
                ) : (
                  <div className="w-20 h-12 rounded bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-700">
                    <ImageIcon className="w-4 h-4" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-sky-100">{ad.advertiser_name}</p>
                  <p className="text-[11px] text-slate-500 font-mono truncate">{ad.slot_key || ad.slot} · {ad.impressions || 0}👁 · {ad.clicks || 0}🖱 · ${ad.price_usd || 0}/mo</p>
                </div>
                <button
                  data-testid={`btn-toggle-ad-${ad.id}`}
                  onClick={() => toggle(ad.id, !ad.active)}
                  className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border ${
                    ad.active ? "border-emerald-500/40 text-emerald-300" : "border-slate-700 text-slate-500"
                  }`}
                >
                  {ad.active ? "● active" : "○ paused"}
                </button>
                <IconBtn testid={`btn-delete-ad-${ad.id}`} onClick={() => remove(ad.id)} icon={Trash2} danger title="Delete ad" />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------------- User Management (role + ban) ---------------- */
function UsersTab({ showToast, currentUserEmail }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/owner/users");
      setUsers(data);
    } catch (e) { showToast(formatApiErrorDetail(e.response?.data?.detail) || "Failed", "err"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const changeRole = async (id, role) => {
    try {
      await api.patch(`/owner/users/${id}/role`, { role });
      setUsers((prev) => prev.map((u) => u.id === id ? { ...u, role } : u));
      showToast(`Role updated → ${role}`);
    } catch (e) { showToast(formatApiErrorDetail(e.response?.data?.detail) || "Failed", "err"); }
  };
  const toggleBan = async (id, banned) => {
    if (banned && !window.confirm("Ban this user? They will be immediately signed out and unable to log back in.")) return;
    try {
      await api.patch(`/owner/users/${id}/ban`, { banned });
      setUsers((prev) => prev.map((u) => u.id === id ? { ...u, banned } : u));
      showToast(banned ? "User banned." : "User restored.");
    } catch (e) { showToast(formatApiErrorDetail(e.response?.data?.detail) || "Failed", "err"); }
  };
  if (loading) return <LoadingRow label="Loading users…" />;
  return (
    <Card icon={Users} title={`Users · ${users.length}`} subtitle="Change roles or ban/restore users. The platform owner (ADMIN_EMAIL) is protected.">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-900">
            <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-slate-500">
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Articles</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isSelf = u.email === currentUserEmail;
              return (
                <tr key={u.id} data-testid={`owner-user-${u.id}`} className="border-t border-slate-800 hover:bg-slate-900/40">
                  <td className="px-3 py-2 font-mono text-xs text-sky-100 truncate">{u.email}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${
                      u.role === "owner" ? "border-sky-400 text-sky-300" :
                      u.role === "admin" ? "border-sky-400/50 text-sky-300" :
                      "border-slate-700 text-slate-400"
                    }`}>{u.role}</span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{u.article_count}</td>
                  <td className="px-3 py-2">
                    {u.banned ? (
                      <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded border border-red-500/40 text-red-300">● banned</span>
                    ) : (
                      <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded border border-emerald-500/40 text-emerald-300">● active</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2 justify-end">
                      {isSelf ? (
                        <span className="text-[10px] text-slate-600 font-mono">— you (owner)</span>
                      ) : (
                        <>
                          <select
                            data-testid={`owner-role-${u.id}`}
                            value={u.role}
                            onChange={(e) => changeRole(u.id, e.target.value)}
                            disabled={u.banned}
                            className="bg-slate-900 border border-slate-800 rounded px-2 py-1 text-xs text-sky-100"
                          >
                            <option value="user">user</option>
                            <option value="admin">admin</option>
                            <option value="owner">owner</option>
                          </select>
                          <button
                            data-testid={`btn-ban-${u.id}`}
                            onClick={() => toggleBan(u.id, !u.banned)}
                            title={u.banned ? "Restore user" : "Ban user"}
                            className={`p-1.5 rounded border text-xs ${
                              u.banned ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" : "border-red-500/40 text-red-300 hover:bg-red-500/10"
                            }`}
                          >
                            {u.banned ? <ShieldCheck className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ---------------- Pricing Plans ---------------- */
function PricingTab({ showToast }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/owner/pricing-plans");
        setPlans(data.plans || []);
      } catch (e) { showToast(formatApiErrorDetail(e.response?.data?.detail) || "Failed", "err"); }
      finally { setLoading(false); }
    })();
  }, []); // eslint-disable-line
  const update = (idx, patch) => setPlans((prev) => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/owner/pricing-plans", { plans });
      showToast("Pricing saved. The public /pricing page now reflects changes.");
    } catch (e) { showToast(formatApiErrorDetail(e.response?.data?.detail) || "Failed", "err"); }
    finally { setSaving(false); }
  };
  if (loading) return <LoadingRow label="Loading plans…" />;
  return (
    <Card icon={Tag} title="Pricing plans" subtitle="Prices, features, and highlight state sync to the public /pricing page instantly on save.">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {plans.map((p, idx) => (
          <div key={p.slug} data-testid={`plan-card-${p.slug}`} className={`border rounded-xl p-5 ${p.highlight ? "border-sky-400/60 bg-sky-500/5" : "border-slate-800"}`}>
            <input
              data-testid={`plan-name-${p.slug}`}
              value={p.name}
              onChange={(e) => update(idx, { name: e.target.value })}
              className="w-full bg-transparent border-b border-slate-800 focus:border-sky-400 pb-1 mb-3 text-sky-100 font-serif text-xl outline-none"
            />
            <div className="grid grid-cols-2 gap-2 mb-3">
              <Field label="Monthly">
                <div className="flex items-center gap-1">
                  <span className="text-sky-300 font-mono">$</span>
                  <input
                    data-testid={`plan-monthly-${p.slug}`}
                    type="number" min="0"
                    value={p.monthly}
                    onChange={(e) => update(idx, { monthly: Number(e.target.value) })}
                    className={`${inputCls} font-mono`}
                  />
                </div>
              </Field>
              <Field label="Yearly">
                <div className="flex items-center gap-1">
                  <span className="text-sky-300 font-mono">$</span>
                  <input
                    data-testid={`plan-yearly-${p.slug}`}
                    type="number" min="0"
                    value={p.yearly}
                    onChange={(e) => update(idx, { yearly: Number(e.target.value) })}
                    className={`${inputCls} font-mono`}
                  />
                </div>
              </Field>
            </div>
            <Field label="Tagline">
              <input
                data-testid={`plan-tagline-${p.slug}`}
                value={p.tagline || ""}
                onChange={(e) => update(idx, { tagline: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Features (one per line)" className="mt-3">
              <textarea
                data-testid={`plan-features-${p.slug}`}
                rows={5}
                value={(p.features || []).join("\n")}
                onChange={(e) => update(idx, { features: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                className={`${inputCls} text-xs`}
              />
            </Field>
            <div className="flex items-center gap-4 mt-3">
              <label className="flex items-center gap-2 text-xs text-slate-400">
                <input
                  type="checkbox"
                  data-testid={`plan-highlight-${p.slug}`}
                  checked={!!p.highlight}
                  onChange={(e) => update(idx, { highlight: e.target.checked })}
                  className="accent-sky-400"
                />
                Highlight
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-400">
                <input
                  type="checkbox"
                  data-testid={`plan-active-${p.slug}`}
                  checked={!!p.active}
                  onChange={(e) => update(idx, { active: e.target.checked })}
                  className="accent-sky-400"
                />
                Active
              </label>
            </div>
          </div>
        ))}
      </div>
      <SaveButton onClick={save} saving={saving} testid="btn-save-pricing" label="Save all plans" />
    </Card>
  );
}

/* ---------------- Auto-Content Engine ---------------- */
function EngineTab({ showToast }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { (async () => setSettings((await api.get("/owner/settings")).data))(); }, []);
  if (!settings) return <LoadingRow label="Loading engine settings…" />;
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/owner/settings", {
        auto_content_enabled: settings.auto_content_enabled,
        auto_content_daily_count: settings.auto_content_daily_count,
        default_amazon_tag: settings.default_amazon_tag,
      });
      showToast("Engine settings saved.");
    } catch (e) { showToast(formatApiErrorDetail(e.response?.data?.detail) || "Failed", "err"); }
    finally { setSaving(false); }
  };
  return (
    <Card icon={Cog} title="Auto-Content Engine" subtitle="Publishes N articles every 24 hours using the platform default Amazon tag.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex items-center gap-3 border border-slate-800 rounded p-4 hover:border-sky-400/40 transition cursor-pointer">
          <input
            type="checkbox"
            data-testid="engine-toggle"
            checked={!!settings.auto_content_enabled}
            onChange={(e) => setSettings({ ...settings, auto_content_enabled: e.target.checked })}
            className="accent-sky-400 w-4 h-4"
          />
          <div>
            <p className="text-sm text-sky-100">Auto-content engine</p>
            <p className="text-[11px] text-slate-500">Generate {settings.auto_content_daily_count || 5} articles every 24 hours</p>
          </div>
          {settings.auto_content_enabled ? (
            <CheckCircle2 className="w-4 h-4 ml-auto text-emerald-400" />
          ) : (
            <XCircle className="w-4 h-4 ml-auto text-slate-600" />
          )}
        </label>
        <Field label="Articles per day">
          <input
            type="number"
            data-testid="engine-count"
            min="1" max="20"
            value={settings.auto_content_daily_count || 5}
            onChange={(e) => setSettings({ ...settings, auto_content_daily_count: parseInt(e.target.value) || 5 })}
            className={`${inputCls} font-mono`}
          />
        </Field>
        <Field label="Default Amazon tag (fallback)" className="md:col-span-2">
          <input
            data-testid="engine-tag"
            value={settings.default_amazon_tag || ""}
            onChange={(e) => setSettings({ ...settings, default_amazon_tag: e.target.value })}
            placeholder="allbusiness-20"
            className={`${inputCls} font-mono`}
          />
        </Field>
      </div>
      <SaveButton onClick={save} saving={saving} testid="btn-save-engine" />
    </Card>
  );
}

/* ---------------- Shared UI helpers ---------------- */
const inputCls = "w-full bg-slate-900 border border-slate-800 rounded px-3 py-2 text-sky-50 focus:border-sky-400 outline-none text-sm transition";

function Card({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="border border-slate-800 rounded-xl p-6 mb-6 last:mb-0 bg-gradient-to-b from-slate-900/40 to-transparent">
      <div className="flex items-start gap-3 mb-4">
        {Icon && <Icon className="w-4 h-4 text-sky-400 mt-1" />}
        <div>
          <h3 className="font-serif text-lg text-sky-100">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}
function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500 block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
function IconBtn({ icon: Icon, onClick, title, testid, danger }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`p-1.5 rounded border text-xs ${danger ? "border-red-500/40 text-red-300 hover:bg-red-500/10" : "border-slate-700 text-slate-400 hover:text-sky-300 hover:border-sky-500/40"}`}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}
function SaveButton({ onClick, saving, testid = "btn-save", label = "Save", icon: Icon = Save }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={saving}
      className="mt-4 px-5 py-2.5 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded font-medium flex items-center gap-2 disabled:opacity-60 transition"
    >
      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />} {label}
    </button>
  );
}
function LoadingRow({ label }) {
  return (
    <div className="text-center py-12 text-slate-500 flex items-center justify-center gap-3">
      <Loader2 className="w-5 h-5 animate-spin" /> {label}
    </div>
  );
}
function EmptyRow({ label }) {
  return <div className="text-center py-8 text-slate-600 text-sm font-mono">{label}</div>;
}

function AdImageDropzone({ preview, onFile, onClear }) {
  const [dragging, setDragging] = React.useState(false);
  const inputRef = React.useRef(null);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) onFile(file);
  };

  if (preview) {
    return (
      <div className="border border-slate-800 rounded-lg p-4 bg-slate-900/40" data-testid="ad-image-preview">
        <div className="flex items-start gap-3">
          <img src={preview} alt="preview" className="max-h-24 rounded border border-slate-800 bg-slate-950" />
          <div className="flex-1">
            <p className="text-xs text-sky-100">Image ready</p>
            <p className="text-[10px] text-slate-500 font-mono mt-1">
              {(preview.length * 0.75 / 1024).toFixed(1)} KB base64
            </p>
            <button
              data-testid="btn-ad-image-clear"
              onClick={onClear}
              type="button"
              className="mt-2 text-[10px] text-red-300 hover:text-red-200 font-mono uppercase tracking-widest"
            >
              ✕ Remove & re-upload
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="ad-image-dropzone"
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ${
        dragging
          ? "border-sky-400 bg-sky-500/5"
          : "border-slate-800 hover:border-sky-500/40 hover:bg-slate-900/40"
      }`}
    >
      <input
        ref={inputRef}
        data-testid="ad-image-file-input"
        type="file"
        accept="image/*"
        onChange={(e) => onFile(e.target.files?.[0])}
        className="hidden"
      />
      <div className="text-slate-500">
        <p className="text-sm text-sky-100 font-medium">Drop image here</p>
        <p className="text-xs mt-1">or click to browse</p>
        <p className="text-[10px] font-mono uppercase tracking-widest text-slate-600 mt-3">
          PNG · JPG · WEBP · max 500KB
        </p>
      </div>
    </div>
  );
}
